import React, { useEffect, useState } from 'react'
import PropTypes from 'prop-types'
import gql from 'graphql-tag'
import { useMutation, useQuery } from 'react-apollo'
import { t } from 'src/lib/i18n-react'

import MyCourse from 'src/pages/MyCourse/MyCourse'
import MyCoursePlaceholder from 'src/pages/MyCourse/MyCoursePlaceholder'
import MyCourseHeader from 'src/pages/MyCourse/subcomponents/MyCourseHeader/MyCourseHeader'
import DataFetchError from 'src/ui/DataFetchError/DataFetchError'
import PageStickyHeader from 'src/ui/PageStickyHeader/PageStickyHeader'

export const FETCH_CEFR_LEVELS = gql`
  query FETCH_CEFR_LEVELS($l2Code: LanguageEnum!) {
    currentUser {
      currentCefrLevel(code: $l2Code) {
        code
        label
        cefrLevelProgress
      }
      unlockedCefrLevels(code: $l2Code) {
        code
        label
        cefrLevelProgress
      }
    }
  }
`

export const FETCH_MY_COURSE = gql`
  query FETCH_MY_COURSE(
    $l1Code: LanguageEnum!
    $l2Code: LanguageEnum!
    $cefrLevel: CefrLevelCode
  ) {
    myCourse(native: $l1Code, learning: $l2Code, cefrLevel: $cefrLevel) {
      id
      name
      iconUrl
      label(format: "unit_sequence_number")
      topic: level {
        name
        isQueueable
      }
      progress {
        isQueued
        isCurrent
        isCompleted
        isSkipped
        exercises
        percentOfExercisesSeen
        flashcards
        media
        writing
        concepts
      }
    }
  }
`

const REMOVE_UNIT_FROM_QUEUE = gql`
  mutation REMOVE_UNIT_FROM_QUEUE($unitInput: UnitInputType!) {
    response: removeUnitFromQueue(unit: $unitInput) {
      status
      errors {
        code
        message
      }
    }
  }
`

const MOVE_UNIT_TO_POSITION = gql`
  mutation MOVE_UNIT_TO_POSITION($unitInput: UnitInputType!, $position: Int!) {
    response: moveUnitToPosition(
      move: { unit: $unitInput, position: $position }
    ) {
      errors {
        code
        message
      }
    }
  }
`

const MyCourseLazy = ({
  l1Code,
  l2Code,
  cefrLevel,
  onShowSnackbar,
  onRenderHeader,
}) => {
  const [isEditMode, setEditMode] = useState(false)
  const [removeUnitFromQueue, { loading: removeMutationLoading }] = useMutation(
    REMOVE_UNIT_FROM_QUEUE
  )
  const [moveUnitToPosition, { loading: reorderMutationLoading }] = useMutation(
    MOVE_UNIT_TO_POSITION
  )

  const response = useQuery(FETCH_MY_COURSE, {
    variables: {
      l1Code,
      l2Code,
      cefrLevel,
    },
    fetchPolicy: 'network-only',
  })
  const { data, loading, refetch } = response
  const units = data?.myCourse

  const responseCefrLevels = useQuery(FETCH_CEFR_LEVELS, {
    variables: { l2Code },
    fetchPolicy: 'network-only',
  })

  const currentCefrLevel =
    responseCefrLevels?.data?.currentUser?.currentCefrLevel?.code

  const handleReorder = async ({ unit, to }) => {
    if (reorderMutationLoading) {
      return
    }

    const errorMessage = t(
      'Error while reordering units. Please try again later.'
    )

    try {
      const unitInput = { id: unit.id }
      const options = {
        variables: { unitInput, position: to },
      }
      const mutationResponse = await moveUnitToPosition(options)

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        onShowSnackbar({ message: errorMessage, type: 'error' })
      }
    } catch (error) {
      console.log(error)
      onShowSnackbar({ message: errorMessage, type: 'error' })
    } finally {
      refetch()
    }
  }

  const handleRemove = async (unit) => {
    if (removeMutationLoading) {
      return
    }

    const errorMessage = t(
      'Error while removing unit from course. Please try again later.'
    )

    try {
      const unitInput = { id: unit.id }
      const options = {
        variables: { unitInput },
      }
      const mutationResponse = await removeUnitFromQueue(options)

      if (
        !mutationResponse.data?.response ||
        mutationResponse.data?.response?.errors?.length
      ) {
        onShowSnackbar({ message: errorMessage, type: 'error' })
      } else {
        refetch()
      }
    } catch (error) {
      console.log(error)
      onShowSnackbar({ message: errorMessage, type: 'error' })
    }
  }

  useEffect(() => {
    if (!loading && units && !responseCefrLevels.loading) {
      const isQueueable = units.every((unit) => unit?.topic?.isQueueable)
      const cefrLevels =
        responseCefrLevels?.data?.currentUser?.unlockedCefrLevels || []
      let selectedCefrLevel = cefrLevels.find(
        (level) => level.code === cefrLevel
      )
      if (!selectedCefrLevel) {
        selectedCefrLevel =
          responseCefrLevels?.data?.currentUser?.currentCefrLevel
      }
      onRenderHeader(
        <PageStickyHeader>
          <MyCourseHeader
            onToggleEditMode={setEditMode}
            isEditMode={isEditMode}
            hideActions={!isQueueable}
            cefrLevels={cefrLevels}
            selectedCefrLevel={selectedCefrLevel}
            currentCefrLevel={
              responseCefrLevels?.data?.currentUser?.currentCefrLevel
            }
            courseListEmpty={!units.length}
          />
        </PageStickyHeader>
      )
    }
  }, [
    isEditMode,
    loading,
    units,
    onRenderHeader,
    setEditMode,
    responseCefrLevels,
  ])

  if (loading && !units) {
    // show only during initial rendering
    return <MyCoursePlaceholder />
  }

  if (units) {
    return (
      <MyCourse
        units={units}
        isEditMode={isEditMode}
        onReorder={handleReorder}
        onRemove={handleRemove}
        currentCefrLevel={currentCefrLevel}
      />
    )
  }

  return <DataFetchError graphQLResponse={response} />
}

MyCourseLazy.propTypes = {
  l1Code: PropTypes.string.isRequired,
  l2Code: PropTypes.string.isRequired,
  cefrLevel: PropTypes.string,
  onShowSnackbar: PropTypes.func,
  onRenderHeader: PropTypes.func,
}

export default MyCourseLazy
